import { useState } from 'react';
import { cx } from './cx';

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Stable 1..6 tint for a name, so the same person always gets the same fallback colour. */
export function tintIndex(name: string): number {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return (h % 6) + 1;
}

/**
 * Person avatars are circles, organisation logos rounded squares (shape encodes kind). A broken or
 * missing image falls back to tinted initials; the accessible name is always the person's name.
 */
export function Avatar({
  name,
  src,
  size = 40,
  shape = 'circle',
  ring = false,
  className,
}: {
  name: string;
  src?: string | null;
  size?: number;
  shape?: 'circle' | 'square';
  /** Sheet-coloured ring, for avatars that overlap a banner. */
  ring?: boolean;
  className?: string;
}) {
  const [broken, setBroken] = useState<string | null>(null);
  const radius = shape === 'circle' ? '999px' : `${Math.max(4, Math.round(size * 0.22))}px`;
  const style = {
    width: size,
    height: size,
    borderRadius: radius,
    fontSize: Math.max(11, size * 0.38),
    boxShadow: ring ? `0 0 0 ${size >= 64 ? 4 : 2}px var(--sheet)` : undefined,
  };
  if (src && broken !== src) {
    return (
      <img
        src={src}
        alt={name}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        data-shape={shape}
        className={cx('flex-none bg-paper-2 object-cover', className)}
        onError={() => setBroken(src)}
        style={style}
      />
    );
  }
  return (
    <span
      role="img"
      aria-label={name}
      data-shape={shape}
      className={cx(
        'inline-flex flex-none items-center justify-center font-sans font-semibold tracking-tight text-ink select-none',
        className,
      )}
      style={{ ...style, background: `var(--tint-${tintIndex(name)})` }}
    >
      <span aria-hidden="true">{initials(name)}</span>
    </span>
  );
}
